import axios from 'axios'
import * as HttpStatus from 'http-status-codes'

import { toCamelCase } from '../helpers/utils'
import { error, errorD1, information, kellyGreen, royalBlue, success } from '../theme/colors'

export interface PerformanceScoreRange {
  label: string
  min: number
  max: number
  color: string
  backgroundColor: string
  description: string
}

export const PERFORMANCE_SCORE_RANGE_DEFAULTS: PerformanceScoreRange[] = [
  {
    label: 'Excellent',
    min: 0.94,
    max: 1.0,
    color: kellyGreen,
    backgroundColor: success,
    description: 'Great job! There are no or little factors affecting your score.',
  },
  {
    label: 'Satisfactory',
    min: 0.89,
    max: 0.94,
    color: royalBlue,
    backgroundColor: information,
    description: 'Be attentive. Your score was impacted by some minor factors.',
  },
  {
    label: 'Needs Attention',
    min: -0.01,
    max: 0.89,
    color: errorD1,
    backgroundColor: error,
    description: 'The Safety team will contact you to provide guidance on how to improve your score.',
  },
]

export interface UserPerformanceScore {
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
  }

  eventType: 'On Time Service' | 'On Time Pickup' | 'On Time Delivery'
  eventCategory: 'On Time Performance'

  score: number | null
  scoreLabel: string | null
  scoreColor: string | null
  scoreBackgroundColor: string | null

  dateUpdated: string | null
}

export interface UserPerformanceEvent {
  userId: string
  teamId: string
  stopId: string

  eventId: string | null
  eventType: string
  eventCategory: string | null
  eventDate: string

  jobId: string
  jobHumanId: string
  deliveryDate: string | null
  loadStopDateUpdated: string | null
  firstArrivalDate: string | null
  appointmentStartTime: string | null
  appointmentEndTime: string | null
  stopTypeDetail: string
  stopType: string
  originCityState: string | null
  destinationCityState: string | null

  isOnTime: boolean
  isJobMissingAllStopsData: boolean
  isStopIncludedInScore: boolean

  loadStopCityState: string
  timeFromArrivalToAppointmentStartMinutes: number | null
  timeFromArrivalToAppointmentEndMinutes: number | null
}

export interface PerformanceEvent {
  jobId: string
  jobHumanId: string
  originCityState: string | null
  destinationCityState: string | null
  jobDate: string
  numStops: number
  events: UserPerformanceEvent[]
  status: string
  statusColor: string
}

export function getPerformanceScoreRanges(): Promise<PerformanceScoreRange[] | undefined> {
  return axios
    .get('/api/v3/performance/performance-score-ranges/')
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        if (response.data && Array.isArray(response.data)) {
          return response.data.map((x) => toCamelCase(x)) as PerformanceScoreRange[]
        }
      }

      return PERFORMANCE_SCORE_RANGE_DEFAULTS
    })
    .then((ranges) =>
      ranges.map((x) => ({
        // Convert min and max to percentage if they are less than or equal to 1, i.e. 0.94 -> 94
        ...x,
        min: x.min <= 1 ? x.min * 100 : x.min,
        max: x.max <= 1 ? x.max * 100 : x.max,
      }))
    )
}

export function getPerformanceScoreForUser(userId: string): Promise<UserPerformanceScore[] | undefined> {
  return axios.get(`/api/v3/performance/performance-scorecards/?user_id=${userId}`).then((response) => {
    if (response.status === HttpStatus.OK && response.data) {
      return toCamelCase(response.data) as UserPerformanceScore[]
    }
  })
}

export function getTeamPerformanceScores(): Promise<UserPerformanceScore[] | undefined> {
  return axios.get('/api/v3/performance/team-performance-scorecards/').then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data ? (response.data.map((x: any) => toCamelCase(x)) as UserPerformanceScore[]) : []
    }
  })
}

export function getPerformanceEventsForUser(
  userId: string,
  eventCategory: string
): Promise<PerformanceEvent[] | undefined> {
  return axios
    .get(`/api/v3/performance/performance-events/?user_id=${userId}&event_category=${eventCategory}`)
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        return response.data ? (toCamelCase(response.data) as PerformanceEvent[]) : []
      }
    })
}
